import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronRight, Send, Calendar as CalendarIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteForm() {
  const [destinations, setDestinations] = useState([]);
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    destination: "", travelers: 2, budget: "", message: "", rgpd: false,
  });

  useEffect(() => {
    axios.get(`${API}/destinations`).then(res => setDestinations(res.data)).catch(console.error);
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.rgpd) {
      toast.error("Veuillez accepter la politique de confidentialite");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        dates: date ? format(date, "dd/MM/yyyy") : "Non precise",
        travelers: parseInt(form.travelers) || 2,
      });
      toast.success("Votre demande a ete envoyee avec succes ! Nous vous recontacterons rapidement.");
      setForm({ first_name: "", last_name: "", email: "", phone: "", destination: "", travelers: 2, budget: "", message: "", rgpd: false });
      setDate(null);
    } catch {
      toast.error("Erreur lors de l'envoi. Veuillez reessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="bg-[#EFEBE5] px-6 md:px-12 lg:px-24 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm font-body text-[#6B6B6B]">
          <Link to="/" className="hover:text-[#2D5016]">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#2D5016] font-medium">Demande de devis</span>
        </div>
      </div>

      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#C8A951] font-body text-sm tracking-[0.3em] uppercase mb-3">Votre voyage sur mesure</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2C2C2C] mb-4">Demande de devis</h1>
            <p className="font-body text-[#6B6B6B]">Remplissez ce formulaire et nous vous recontacterons sous 48h avec un devis personnalise.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-body text-sm text-[#2C2C2C] mb-1.5">Prenom *</label>
                <Input
                  value={form.first_name} onChange={e => handleChange("first_name", e.target.value)}
                  className="rounded-lg border-[#DBCFB0] bg-white font-body" required
                  data-testid="quote-first-name"
                />
              </div>
              <div>
                <label className="block font-body text-sm text-[#2C2C2C] mb-1.5">Nom *</label>
                <Input
                  value={form.last_name} onChange={e => handleChange("last_name", e.target.value)}
                  className="rounded-lg border-[#DBCFB0] bg-white font-body" required
                  data-testid="quote-last-name"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-body text-sm text-[#2C2C2C] mb-1.5">Email *</label>
                <Input
                  type="email" value={form.email} onChange={e => handleChange("email", e.target.value)}
                  className="rounded-lg border-[#DBCFB0] bg-white font-body" required
                  data-testid="quote-email"
                />
              </div>
              <div>
                <label className="block font-body text-sm text-[#2C2C2C] mb-1.5">Telephone *</label>
                <Input
                  type="tel" value={form.phone} onChange={e => handleChange("phone", e.target.value)}
                  className="rounded-lg border-[#DBCFB0] bg-white font-body" required
                  data-testid="quote-phone"
                />
              </div>
            </div>

            <div>
              <label className="block font-body text-sm text-[#2C2C2C] mb-1.5">Destination souhaitee *</label>
              <Select value={form.destination} onValueChange={v => handleChange("destination", v)} required>
                <SelectTrigger className="rounded-lg border-[#DBCFB0] bg-white font-body" data-testid="quote-destination-select">
                  <SelectValue placeholder="Choisissez une destination" />
                </SelectTrigger>
                <SelectContent>
                  {destinations.map(d => (
                    <SelectItem key={d.slug} value={d.name}>{d.name} - {d.duration} jours</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-body text-sm text-[#2C2C2C] mb-1.5">Nombre de voyageurs</label>
                <Input
                  type="number" min="1" max="20" value={form.travelers}
                  onChange={e => handleChange("travelers", e.target.value)}
                  className="rounded-lg border-[#DBCFB0] bg-white font-body"
                  data-testid="quote-travelers"
                />
              </div>
              <div>
                <label className="block font-body text-sm text-[#2C2C2C] mb-1.5">Dates souhaitees</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start rounded-lg border-[#DBCFB0] bg-white font-body text-left" data-testid="quote-date-picker">
                      <CalendarIcon className="w-4 h-4 mr-2 text-[#6B6B6B]" />
                      {date ? format(date, "dd MMMM yyyy", { locale: fr }) : "Selectionnez une date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate} locale={fr} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div>
              <label className="block font-body text-sm text-[#2C2C2C] mb-1.5">Budget approximatif</label>
              <Select value={form.budget} onValueChange={v => handleChange("budget", v)}>
                <SelectTrigger className="rounded-lg border-[#DBCFB0] bg-white font-body" data-testid="quote-budget">
                  <SelectValue placeholder="Selectionnez votre budget" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="moins-2000">Moins de 2 000 EUR</SelectItem>
                  <SelectItem value="2000-3000">2 000 - 3 000 EUR</SelectItem>
                  <SelectItem value="3000-4000">3 000 - 4 000 EUR</SelectItem>
                  <SelectItem value="plus-4000">Plus de 4 000 EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block font-body text-sm text-[#2C2C2C] mb-1.5">Message / Demandes speciales</label>
              <Textarea
                value={form.message} onChange={e => handleChange("message", e.target.value)}
                className="rounded-lg border-[#DBCFB0] bg-white font-body min-h-[120px]"
                placeholder="Vos envies, contraintes, dates flexibles..."
                data-testid="quote-message"
              />
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="rgpd" checked={form.rgpd}
                onCheckedChange={v => handleChange("rgpd", v)}
                className="mt-0.5 border-[#DBCFB0] data-[state=checked]:bg-[#2D5016]"
                data-testid="quote-rgpd"
              />
              <label htmlFor="rgpd" className="font-body text-sm text-[#6B6B6B] leading-relaxed cursor-pointer">
                J'accepte que mes donnees soient utilisees pour repondre a ma demande conformement a la{" "}
                <Link to="/politique-de-confidentialite" className="text-[#2D5016] underline">politique de confidentialite</Link>.
              </label>
            </div>

            <Button
              type="submit" disabled={submitting}
              className="w-full bg-[#2D5016] text-[#FDF8F0] rounded-full py-4 text-lg font-body font-medium hover:bg-[#2D5016]/90 shadow-lg disabled:opacity-50"
              data-testid="quote-submit-btn"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Envoi en cours...
                </span>
              ) : (
                <span className="flex items-center gap-2 justify-center">
                  <Send className="w-5 h-5" /> Envoyer ma demande
                </span>
              )}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
